import React from 'react';
import { CreditCard, Loader2, Check, X, AlertTriangle, RefreshCw, Clock, Hand } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import { READER_UNAVAILABLE } from '../../utils/kioskPath';

/**
 * The kiosk's card payment screens, one per payment state
 * (docs/CHECKOUT_API_2026-10-28.md §1). Every word is about what the
 * gateway said - never a success it did not give.
 *
 * phase: pricing | starting | resuming | waiting | cancelling | approved |
 *        partial | declined | timed_out | error | unavailable
 */
const dollars = (cents) => formatCurrency((cents || 0) / 100);

function Icon({ tone, children, pulse }) {
  const ring = {
    amber: 'bg-[#f5a623]/10 text-[#f5a623]',
    green: 'bg-emerald-500/15 text-emerald-400',
    red: 'bg-red-500/15 text-red-400',
    slate: 'bg-white/5 text-white/70',
  }[tone];
  return (
    <div className={`w-28 h-28 rounded-full flex items-center justify-center mb-8 ${ring} ${pulse ? 'animate-pulse' : ''}`}>
      {children}
    </div>
  );
}

function Btn({ kind = 'primary', children, ...rest }) {
  const cls = kind === 'primary'
    ? 'bg-[#f5a623] text-[#0f1419] font-semibold hover:brightness-110'
    : 'bg-white/[0.06] border border-white/10 text-white font-medium hover:bg-white/10';
  return (
    <button type="button" className={`flex-1 min-h-[64px] px-6 rounded-2xl text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-all ${cls}`} {...rest}>
      {children}
    </button>
  );
}

function Countdown({ seconds, label = 'Starting over in' }) {
  if (seconds == null) return null;
  return <p className="mt-6 text-sm text-white/40" data-testid="kiosk-reset-countdown">{label} {seconds}s</p>;
}

export default function PaymentProcessingScreen({
  phase,
  view = null,            // the last PaymentView
  amountCents = 0,        // what this try asks (incl. tip)
  chargedCents = null,    // what the card(s) charged (approved)
  longWait = false,
  message = '',
  resetIn = null,
  noCharge = true,
  onCancel,
  onRetry,
  onPayRemaining,
  onSeeStaff,
  onStartOver,
}) {
  const wrap = (testId, children) => (
    <div className="flex flex-col items-center justify-center py-12 px-8 text-center" data-testid={testId} data-phase={phase}>
      {children}
    </div>
  );

  if (phase === 'pricing' || phase === 'starting' || phase === 'resuming') {
    const title = { pricing: 'Getting your total…', starting: 'Connecting to the card reader…', resuming: 'Checking your payment…' }[phase];
    return wrap('kiosk-pay-busy', <>
      <Icon tone="amber"><Loader2 size={52} className="animate-spin" /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-2">{title}</h2>
      <p className="text-lg text-white/50">One moment please</p>
    </>);
  }

  if (phase === 'waiting') {
    const busy = (view?.message || '').toLowerCase().startsWith('the card terminal is busy');
    return wrap('kiosk-pay-waiting', <>
      <Icon tone="amber" pulse={!longWait}>{longWait ? <Clock size={52} /> : <CreditCard size={52} />}</Icon>
      {longWait ? (
        <>
          <h2 className="text-3xl font-semibold text-white mb-3" data-testid="kiosk-checking-payment">Checking your payment…</h2>
          <p className="text-lg text-white/60 max-w-md">We're waiting for the card reader's answer. Please don't pay again — this screen updates on its own.</p>
        </>
      ) : (
        <>
          <h2 className="text-3xl font-semibold text-white mb-3">Present your card</h2>
          <p className="text-xl text-white/60">Tap, insert or swipe on the card reader</p>
        </>
      )}
      {busy && <p className="mt-4 text-base text-amber-300">{view.message}</p>}
      <p className="text-5xl font-bold text-[#f5a623] mt-8" data-testid="kiosk-pay-amount">{dollars(amountCents)}</p>
      <button type="button" onClick={onCancel}
        className="mt-10 min-h-[56px] px-10 rounded-2xl text-lg text-white/60 hover:text-white border border-white/10 hover:bg-white/5"
        data-testid="kiosk-cancel-payment">
        Cancel
      </button>
    </>);
  }

  if (phase === 'cancelling') {
    return wrap('kiosk-pay-cancelling', <>
      <Icon tone="slate"><Loader2 size={52} className="animate-spin" /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-3">Cancelling…</h2>
      <p className="text-lg text-white/55 max-w-md">Waiting for the card reader to confirm. If your card was already approved, your order goes through.</p>
    </>);
  }

  if (phase === 'approved') {
    const fee = view?.fee_cents || 0;
    return wrap('kiosk-pay-approved', <>
      <Icon tone="green"><Check size={56} strokeWidth={3} /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-3">Payment approved</h2>
      {view?.last4 && <p className="text-lg text-white/60">{view.card_brand || 'Card'} •••• {view.last4}</p>}
      <p className="text-5xl font-bold text-emerald-400 mt-6" data-testid="kiosk-charged-total">{dollars(chargedCents)}</p>
      {fee > 0 && <p className="text-sm text-white/45 mt-2">Includes {dollars(fee)} card fee</p>}
      <p className="text-sm text-white/40 mt-6">Getting your order number…</p>
    </>);
  }

  if (phase === 'partial') {
    const approved = (view?.approved_cents || 0) + (view?.tip_cents || 0) + (view?.fee_cents || 0);
    return wrap('kiosk-pay-partial', <>
      <Icon tone="amber"><AlertTriangle size={52} /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-3" data-testid="kiosk-partial-text">
        Card approved {dollars(approved)} — {dollars(view?.remaining_cents)} remaining
      </h2>
      <p className="text-lg text-white/60 max-w-md mb-10">Your card covered part of the order. Pay the rest with another card, or see staff to finish.</p>
      <div className="flex gap-4 w-full max-w-lg">
        <Btn kind="secondary" onClick={onSeeStaff} data-testid="kiosk-partial-staff">See staff</Btn>
        <Btn onClick={onPayRemaining} data-testid="kiosk-pay-remaining"><CreditCard size={20} /> Pay {dollars(view?.remaining_cents)}</Btn>
      </div>
      <Countdown seconds={resetIn} />
    </>);
  }

  if (phase === 'declined') {
    return wrap('kiosk-pay-declined', <>
      <Icon tone="red"><X size={56} strokeWidth={3} /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-3">Card declined</h2>
      <p className="text-lg text-white/60 max-w-md mb-10">{view?.message && !/declin/i.test(view.message) ? `${view.message}. ` : ''}Your card was not charged. Try again or use another card.</p>
      <div className="flex gap-4 w-full max-w-lg">
        <Btn kind="secondary" onClick={onStartOver} data-testid="kiosk-start-over">Cancel order</Btn>
        <Btn onClick={onRetry} data-testid="kiosk-retry"><RefreshCw size={20} /> Try again</Btn>
      </div>
      <Countdown seconds={resetIn} />
    </>);
  }

  if (phase === 'timed_out') {
    return wrap('kiosk-pay-timed-out', <>
      <Icon tone="amber"><Clock size={52} /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-3">We couldn't confirm your payment</h2>
      <p className="text-lg text-white/60 max-w-md mb-2">The card reader did not answer in time. If your card was charged, this screen will confirm it — please don't pay twice.</p>
      <p className="flex items-center justify-center gap-2 text-sm text-white/45 mb-10"><Loader2 size={14} className="animate-spin" /> Still checking</p>
      <div className="flex gap-4 w-full max-w-lg">
        <Btn kind="secondary" onClick={onSeeStaff} data-testid="kiosk-timeout-staff"><Hand size={20} /> See staff</Btn>
        <Btn onClick={onRetry} data-testid="kiosk-check-again"><RefreshCw size={20} /> Check again</Btn>
      </div>
      <Countdown seconds={resetIn} />
    </>);
  }

  if (phase === 'error') {
    return wrap('kiosk-pay-error', <>
      <Icon tone="red"><AlertTriangle size={52} /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-3">The payment did not go through</h2>
      <p className="text-lg text-white/60 max-w-md mb-10">{view?.message ? `${view.message}. ` : ''}If you see a charge on your card, please see staff.</p>
      <div className="flex gap-4 w-full max-w-lg">
        <Btn kind="secondary" onClick={onSeeStaff} data-testid="kiosk-error-staff">See staff</Btn>
        <Btn onClick={onRetry} data-testid="kiosk-retry"><RefreshCw size={20} /> Try again</Btn>
      </div>
      <Countdown seconds={resetIn} />
    </>);
  }

  if (phase === 'unavailable') {
    return wrap('kiosk-reader-unavailable', <>
      <Icon tone="red"><CreditCard size={52} /></Icon>
      <h2 className="text-3xl font-semibold text-white mb-3" data-testid="kiosk-reader-unavailable-text">{READER_UNAVAILABLE}</h2>
      {message && <p className="text-lg text-white/55 max-w-md mb-2">{message}</p>}
      <p className="text-base text-white/45 max-w-md mb-10">{noCharge ? 'Nothing was charged.' : 'If you see a charge on your card, staff can check it.'}</p>
      <div className="flex gap-4 w-full max-w-sm">
        <Btn kind="secondary" onClick={onStartOver} data-testid="kiosk-unavailable-done">Done</Btn>
      </div>
      <Countdown seconds={resetIn} />
    </>);
  }

  return null;
}
